/**
 * LA COLA DE SUBIDA — el estado de cada archivo, sin React.
 *
 * ## El problema que resuelve
 *
 * El diálogo subía veinte archivos con un único spinner. Las subidas son **secuenciales a
 * propósito** —el orden de la galería sale del orden en que terminan, ver
 * `useUploadDocumentsMutation`— así que veinte archivos son varios minutos de pantalla quieta
 * en la que no se sabe si va por el segundo o por el decimoctavo, ni cuál de los veinte fue
 * el que falló. Un lote parcial sin decir cuál falló obliga a comparar la lista a mano, y
 * volver a subir «por las dudas» duplica lo que sí entró.
 *
 * ## Por qué el progreso es por ARCHIVO y no por bytes
 *
 * Porque los bytes serían una mentira medible. La subida viaja por el cliente generado del
 * contrato (`fetch`), y `fetch` **no publica progreso de subida**: para tener una barra por
 * bytes habría que salirse del cliente generado y rehacer la llamada con `XMLHttpRequest`,
 * duplicando la serialización del `multipart` y el manejo de errores del contrato. Una barra
 * animada sin datos detrás —el atajo habitual— es peor que no tenerla: promete precisión que
 * no existe y deja de moverse justo cuando el archivo grande se está tomando su tiempo.
 *
 * Lo que sí es verdad, y es lo que hace falta saber, es **en cuál va y cuáles fallaron**. Eso
 * es lo que este módulo modela: un estado por archivo y un avance `hechos/total` exacto.
 */

/** En qué está cada archivo del lote. */
export const UPLOAD_ITEM_STATUS = {
  /** Elegido, todavía sin turno. */
  queued: 'queued',
  /** Es el que está viajando ahora. Hay a lo sumo uno: el lote es secuencial. */
  uploading: 'uploading',
  /** Entró. Si el lote pedía publicar y no se pudo, se distingue con `unpublished`. */
  done: 'done',
  /** No entró. `error` dice por qué. */
  failed: 'failed'
} as const

export type UploadItemStatus = (typeof UPLOAD_ITEM_STATUS)[keyof typeof UPLOAD_ITEM_STATUS]

export type UploadQueueItem = {
  /** Identidad del ARCHIVO, no su posición: sacar uno del medio no puede renombrar a los de abajo. */
  key: string
  name: string
  size: number
  status: UploadItemStatus
  /** Mensaje del servidor cuando `failed`. Se muestra en la fila, no en un toast que se va. */
  error?: string
  /** Subió pero no se pudo publicar. El archivo ESTÁ: no hay que volver a subirlo. */
  unpublished?: boolean
}

/**
 * La identidad de un archivo elegido.
 *
 * Nombre + tamaño + fecha de modificación, y no el índice: el picker no sabe editar, así que
 * sacar un archivo del medio corre a todos los de abajo y una clave posicional haría que React
 * reutilice el nodo equivocado. Dos archivos distintos con los tres valores iguales son, a
 * todos los efectos de esta pantalla, el mismo archivo.
 */
export function uploadItemKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}

export function createQueue(files: File[]): UploadQueueItem[] {
  return files.map((file) => ({
    key: uploadItemKey(file),
    name: file.name,
    size: file.size,
    status: UPLOAD_ITEM_STATUS.queued
  }))
}

/** Marca uno y devuelve una cola nueva. Inmutable: la cola es estado de React. */
export function markItem(
  queue: UploadQueueItem[],
  key: string,
  patch: Partial<Omit<UploadQueueItem, 'key'>>
): UploadQueueItem[] {
  return queue.map((item) => (item.key === key ? { ...item, ...patch } : item))
}

export type UploadProgress = {
  total: number
  /** Terminados, bien o mal: es lo que hace avanzar la barra. */
  settled: number
  succeeded: number
  failed: number
  /** 0–100, para la barra general. `100` con la cola vacía: no hay nada pendiente. */
  percent: number
  /** El nombre del que está viajando, para decir «Subiendo <nombre>…». */
  current: string | null
}

export function queueProgress(queue: UploadQueueItem[]): UploadProgress {
  const total = queue.length
  const succeeded = queue.filter((item) => item.status === UPLOAD_ITEM_STATUS.done).length
  const failed = queue.filter((item) => item.status === UPLOAD_ITEM_STATUS.failed).length
  const settled = succeeded + failed
  const current = queue.find((item) => item.status === UPLOAD_ITEM_STATUS.uploading)?.name ?? null

  return {
    total,
    settled,
    succeeded,
    failed,
    percent: total === 0 ? 100 : Math.round((settled / total) * 100),
    current
  }
}

/**
 * Suma archivos a una selección SIN perder los que ya estaban.
 *
 * Es lo que separa un dropzone de un `<input type="file">`: soltar una segunda tanda tiene
 * que AGREGAR, no reemplazar. El picker nativo reemplaza —es su comportamiento, no se puede
 * cambiar—, así que la acumulación vive acá y las dos puertas, soltar y elegir, entran por el
 * mismo camino.
 *
 * Los repetidos se descartan en silencio: quien suelta la misma carpeta dos veces no está
 * pidiendo subir todo dos veces, y avisarlo con un error convertiría un gesto inocente en una
 * corrección.
 */
export function mergeSelection(current: File[], incoming: File[]): File[] {
  const seen = new Set(current.map(uploadItemKey))
  const added = incoming.filter((file) => {
    const key = uploadItemKey(file)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return [...current, ...added]
}

/**
 * Parte la selección en la que entra y la que sobra por PESO.
 *
 * Se avisa acá y no después del `413`: un archivo grande tarda en subir justo para volver
 * rechazado. Y se NOMBRAN los que sobran en vez de decir «alguno es muy grande»: con ocho
 * elegidos, saber cuál es la mitad del arreglo.
 */
export function splitByMaxSize(
  files: File[],
  maxBytes: number
): { accepted: File[]; rejected: File[] } {
  return {
    accepted: files.filter((file) => file.size <= maxBytes),
    rejected: files.filter((file) => file.size > maxBytes)
  }
}
